import { useState, useId, useRef } from 'react';
import { Storage } from '../storage';
import { COLOR_PRESETS } from '../constants';
import Icon from './Icon';

// ─── PALETTE GROUPS ───────────────────────────────────────────────────────────
//  Three semantic sections shown in the picker with labeled dividers.

const PRESET_GROUPS = [
  {
    label: 'Developer Favorites',
    names: [
      'Material Design', 'Tailwind Default', 'Nord Arctic Blue',
      'Solarized', 'Dracula', 'Monokai', 'Gruvbox',
    ],
  },
  {
    label: 'Night Mode',
    names: [
      'One Dark', 'Tokyo Night', 'Catppuccin Mocha',
      'GitHub Dark', 'Rose Pine', 'Night Owl', 'Ayu Dark',
    ],
  },
  {
    label: 'Mood & Aesthetic',
    names: [
      'Retro Sunset', "Synthwave '84", 'Neon Cyberpunk',
      'Tropical Punch', 'Berry and Cream', 'Pantone Classics',
    ],
  },
];

// ─── COLOR CUSTOMIZER COMPONENT ───────────────────────────────────────────────
//
//  Allows the user to style a timeline with color presets, individual color
//  pickers, and an optional background image upload.
//
//  Props:
//    colors           {object}     { primary, secondary, tertiary }
//    bgImage          {string|null}  Supabase Storage URL or null
//    onChange         {function}   receives updated colors object or preset object
//    onBgChange       {function?}  receives URL string or null (to remove)
//    onPreviewColor   {function?}  called with preset colors when a swatch is hovered
//    onClearPreview   {function?}  called when the mouse leaves the swatch grid
//

const ColorCustomizer = ({
  colors,
  bgImage,
  onChange,
  onBgChange,
  onPreviewColor,
  onClearPreview,
}) => {
  const fileRef  = useRef();
  const idPrefix = useId();
  const [hoveredName, setHoveredName] = useState(null);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const url = await Storage.uploadImage(file, 'background');
      onBgChange?.(url);
    } catch (err) {
      console.error('Background image upload failed:', err.message);
    }
  };

  // Which preset currently matches the saved colors?
  const matchedPreset = COLOR_PRESETS.find(
    p => p.primary === colors.primary &&
         p.secondary === colors.secondary &&
         p.tertiary === colors.tertiary
  );

  const displayName = hoveredName || matchedPreset?.name || null;

  return (
    <div>
      <p style={{ color: 'var(--text2)', fontSize: 13, marginBottom: 20 }}>
        Choose a preset or fine-tune individual colors.
      </p>

      {/* ── Presets ── */}
      <fieldset style={{ border: 'none', padding: 0, margin: '0 0 24px' }}>
        <legend style={{
          fontSize: 12, color: 'var(--text2)', fontWeight: 500,
          letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 16,
        }}>
          Presets
        </legend>

        {PRESET_GROUPS.map((group, gi) => {
          const groupPresets = group.names
            .map(name => COLOR_PRESETS.find(p => p.name === name))
            .filter(Boolean);

          return (
            <div key={group.label} style={gi > 0 ? { marginTop: 18 } : {}}>
              {/* Section label + divider */}
              <div className="cc-group-header">
                <span className="cc-group-label">{group.label}</span>
                <span className="cc-group-rule" aria-hidden="true" />
              </div>

              {/* Swatch grid */}
              <div
                className="color-preset-grid"
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(88px, 1fr))',
                  gap: 7,
                }}
              >
                {groupPresets.map(preset => {
                  const isActive =
                    colors.primary   === preset.primary &&
                    colors.secondary === preset.secondary &&
                    colors.tertiary  === preset.tertiary;

                  return (
                    <button
                      key={preset.name}
                      className="cc-swatch"
                      type="button"
                      onClick={() => {
                        onChange(preset);
                        onClearPreview?.();
                      }}
                      onMouseEnter={() => {
                        setHoveredName(preset.name);
                        onPreviewColor?.(preset);
                      }}
                      onMouseLeave={() => {
                        setHoveredName(null);
                        onClearPreview?.();
                      }}
                      aria-pressed={isActive}
                      aria-label={`Apply ${preset.name} color preset`}
                      style={{
                        background:    preset.tertiary,
                        border:        `2px solid ${isActive ? preset.primary : 'transparent'}`,
                        outline:       isActive ? `2px solid ${preset.primary}` : 'none',
                        outlineOffset: 2,
                      }}
                    >
                      {/* Dark / light mode indicator */}
                      <span className="cc-swatch-mode" aria-hidden="true">
                        {preset.mode === 'dark' ? '🌙' : '☀️'}
                      </span>

                      {/* Three-dot color preview */}
                      <div
                        aria-hidden="true"
                        style={{ display: 'flex', gap: 4, justifyContent: 'center', marginBottom: 5 }}
                      >
                        {['primary', 'secondary', 'tertiary'].map(k => (
                          <div
                            key={k}
                            style={{
                              width: 12, height: 12, borderRadius: '50%',
                              background: preset[k],
                              border: '1px solid rgba(128,128,128,0.3)',
                            }}
                          />
                        ))}
                      </div>

                      {/* Palette name */}
                      <span style={{
                        fontSize: 9, color: preset.primary, fontWeight: 600,
                        lineHeight: 1.25, display: 'block',
                      }}>
                        {preset.name}
                      </span>

                      {/* Tooltip — visible on hover after 300 ms (CSS transition-delay) */}
                      <span className="cc-swatch-tooltip" role="tooltip">
                        {preset.name}&nbsp;·&nbsp;{preset.mode === 'dark' ? 'Dark' : 'Light'}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Active / hovered palette name */}
        <p className="cc-active-label">
          {displayName ? (
            <>
              Palette: <strong>{displayName}</strong>
              {hoveredName && <em className="cc-preview-hint">— preview</em>}
            </>
          ) : (
            'Palette: Custom'
          )}
        </p>
      </fieldset>

      {/* ── Custom color pickers ── */}
      <fieldset style={{ border: 'none', padding: 0, margin: '0 0 24px' }}>
        <legend style={{
          fontSize: 12, color: 'var(--text2)', fontWeight: 500,
          letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 10,
        }}>
          Custom Colors
        </legend>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 16 }}>
          {['primary', 'secondary', 'tertiary'].map(key => {
            const pickerId = `${idPrefix}-${key}-picker`;
            const hexId    = `${idPrefix}-${key}-hex`;
            const capKey   = key.charAt(0).toUpperCase() + key.slice(1);
            return (
              <div key={key}>
                <label htmlFor={pickerId}>{capKey} Color</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    id={pickerId}
                    type="color"
                    value={colors[key]}
                    onChange={e => onChange({ ...colors, [key]: e.target.value })}
                    aria-label={`${capKey} color picker`}
                    style={{ width: 44, height: 36, padding: 2, cursor: 'pointer' }}
                  />
                  <input
                    id={hexId}
                    value={colors[key]}
                    onChange={e => onChange({ ...colors, [key]: e.target.value })}
                    aria-label={`${capKey} color hex value`}
                    style={{ flex: 1, fontFamily: 'monospace' }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </fieldset>

      {/* ── Background image ── */}
      {onBgChange && (
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 20 }}>
          <label style={{ marginBottom: 12 }}>
            Background Image{' '}
            <span style={{ color: 'var(--text3)', fontWeight: 400 }}>(optional)</span>
          </label>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <button
              className="btn-ghost"
              type="button"
              onClick={() => fileRef.current.click()}
              aria-label="Upload background image"
              style={{ display: 'flex', alignItems: 'center', gap: 8 }}
            >
              <Icon name="image" size={16} /> Upload Image
            </button>
            {bgImage && (
              <button
                className="btn-danger btn-sm"
                type="button"
                onClick={() => onBgChange(null)}
                aria-label="Remove background image"
              >
                Remove
              </button>
            )}
            {bgImage && (
              <span style={{ fontSize: 12, color: 'var(--success)' }} aria-live="polite">
                ✓ Image set
              </span>
            )}
          </div>
          <input
            type="file"
            ref={fileRef}
            accept="image/*"
            onChange={handleFile}
            aria-label="Background image file input"
            style={{ display: 'none' }}
          />
        </div>
      )}
    </div>
  );
};

export default ColorCustomizer;
